import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, FileText, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CONTENT_TYPES = [
  { value: 'match_start', label: 'Maç Başlangıcı' },
  { value: 'goal_alert', label: 'Gol Bildirimi' },
  { value: 'halftime_summary', label: 'Devre Arası Özeti' },
  { value: 'fulltime_summary', label: 'Maç Sonu Özeti' },
  { value: 'match_preview', label: 'Maç Önizleme' },
  { value: 'match_recap', label: 'Maç Özeti' },
];
const PLATFORMS = ['general', 'telegram', 'twitter', 'instagram', 'web'];

export default function SportsTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', content_type: 'match_preview', platform: 'general', template: '', is_global: true });

  const load = () => {
    const params = filter !== 'all' ? `?content_type=${filter}` : '';
    api.get(`/sports/templates${params}`).then(r => setTemplates(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const handleSave = async () => {
    try {
      if (editId) await api.put(`/sports/templates/${editId}`, form);
      else await api.post('/sports/templates', form);
      toast.success(editId ? 'Güncellendi' : 'Oluşturuldu');
      setShowDialog(false); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/sports/templates/${id}`); toast.success('Silindi'); load(); } catch { toast.error('Hata'); }
  };

  const typeLabel = (t) => CONTENT_TYPES.find(ct => ct.value === t)?.label || t;

  return (
    <div data-testid="sports-templates-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">İçerik Şablonları</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Maç içerik şablonları</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm({ name: '', content_type: 'match_preview', platform: 'general', template: '', is_global: true }); setShowDialog(true); }} className="bg-white text-black hover:bg-[#E5E5E5] h-9 text-sm" data-testid="add-template-btn"><Plus size={14} className="mr-1" /> Şablon Ekle</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-ibm ${filter === 'all' ? 'bg-white text-black' : 'bg-[#141414] text-[#A3A3A3] border border-[#262626]'}`}>Tümü</button>
        {CONTENT_TYPES.map(ct => (
          <button key={ct.value} onClick={() => setFilter(ct.value)} className={`px-3 py-1.5 rounded-full text-xs font-ibm ${filter === ct.value ? 'bg-white text-black' : 'bg-[#141414] text-[#A3A3A3] border border-[#262626]'}`}>{ct.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map(t => (
          <Card key={t.id} className="bg-[#141414] border-[#262626]" data-testid={`tpl-${t.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-ibm font-medium">{t.name}</h3>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 text-xs">{typeLabel(t.content_type)}</Badge>
                    <Badge variant="outline" className="text-[#A3A3A3] border-[#262626] text-xs capitalize">{t.platform}</Badge>
                    {t.is_global && <Badge variant="outline" className="bg-blue-500/10 text-blue-400 text-xs">Global</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setEditId(t.id); setForm(t); setShowDialog(true); }} className="h-7 w-7 p-0 text-[#525252] hover:text-white"><Edit size={12} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="h-7 w-7 p-0 text-[#525252] hover:text-red-400"><Trash2 size={12} /></Button>
                </div>
              </div>
              <pre className="text-xs text-[#737373] font-ibm bg-[#0A0A0A] p-3 rounded border border-[#1A1A1A] whitespace-pre-wrap max-h-[120px] overflow-y-auto">{t.template}</pre>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-outfit text-white">{editId ? 'Şablon Düzenle' : 'Yeni Şablon'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Ad</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-[#A3A3A3] font-ibm text-sm">İçerik Türü</Label>
                <Select value={form.content_type} onValueChange={v => setForm({...form, content_type: v})}><SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">{CONTENT_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-[#A3A3A3] font-ibm text-sm">Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm({...form, platform: v})}><SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">{PLATFORMS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-[#A3A3A3] font-ibm text-sm">Şablon İçeriği</Label>
              <Textarea value={form.template} onChange={e => setForm({...form, template: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[180px] font-jetbrains text-sm" placeholder="Değişkenler: {home_team}, {away_team}, {league}, {match_date}, {venue}, {home_score}, {away_score}, {summary}, {hashtags}, {minute}..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#262626] text-[#A3A3A3]">İptal</Button>
            <Button onClick={handleSave} className="bg-white text-black hover:bg-[#E5E5E5]">{editId ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
